import { useState } from "react";
import { Bot, Check, Coins, Loader2, Pencil, RotateCcw, Users, X } from "lucide-react";
import {
  useAronCosts,
  useCompanyAiSpend,
  useClearUserBudget,
  useSetUserBudget,
  useUpdateGlobalBudget,
} from "../api/aronCostsApi";
import type { AronCostRow, AronProvider } from "../types";

function fmtInt(n: number): string {
  return (n || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

// ARON spend is billed in USD with sub-cent precision — don't round tiny amounts to
// "0,00". Show 2 decimals from $1 up, 4 below, and a floor marker for non-zero dust.
function fmtUsd(value: string | number): string {
  const n = typeof value === "string" ? parseFloat(value) : value;
  if (!n) return "0,00";
  if (Math.abs(n) > 0 && Math.abs(n) < 0.0001) return "<0,0001";
  const decimals = Math.abs(n) >= 1 ? 2 : 4;
  // Group thousands on the integer part ONLY — applying the separator to the whole
  // string would inject spaces into the decimals (e.g. "0,0340" → "0,0 340").
  const [intPart, decPart] = n.toFixed(decimals).split(".");
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  return `${grouped},${decPart}`;
}

function fmtDate(s: string | null): string {
  if (!s) return "—";
  return new Date(s).toLocaleDateString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "numeric",
  });
}

const PROVIDER_BAR: Record<AronProvider, string> = {
  claude: "bg-sky-500",
  deepseek: "bg-violet-500",
};
const PROVIDER_DOT: Record<AronProvider, string> = {
  claude: "bg-sky-500",
  deepseek: "bg-violet-500",
};
const PROVIDER_LABEL: Record<AronProvider, string> = {
  claude: "Claude",
  deepseek: "DeepSeek",
};

function shortModel(id: string): string {
  if (id.toLowerCase().startsWith("deepseek")) return "DeepSeek";
  return id.replace(/^claude-/, "").replace(/-/g, ".");
}

// Two-segment stacked bar (Claude vs DeepSeek) from two USD strings.
function ProviderSplitBar({ claude, deepseek }: { claude: string; deepseek: string }) {
  const c = parseFloat(claude) || 0;
  const d = parseFloat(deepseek) || 0;
  const total = c + d;
  const cPct = total > 0 ? (c / total) * 100 : 0;
  const dPct = total > 0 ? (d / total) * 100 : 0;
  return (
    <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
      <div className={PROVIDER_BAR.claude} style={{ width: `${cPct}%` }} />
      <div className={PROVIDER_BAR.deepseek} style={{ width: `${dPct}%` }} />
    </div>
  );
}

function ProviderLegend({ claude, deepseek }: { claude: string; deepseek: string }) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-500">
      <span className="inline-flex items-center gap-1">
        <span className={`h-2 w-2 rounded-full ${PROVIDER_DOT.claude}`} /> {PROVIDER_LABEL.claude}{" "}
        {fmtUsd(claude)} $
      </span>
      <span className="inline-flex items-center gap-1">
        <span className={`h-2 w-2 rounded-full ${PROVIDER_DOT.deepseek}`} /> {PROVIDER_LABEL.deepseek}{" "}
        {fmtUsd(deepseek)} $
      </span>
    </div>
  );
}

const CARD =
  "rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800";

function SummaryCard({
  icon: Icon,
  label,
  value,
  children,
}: {
  icon: typeof Coins;
  label: string;
  value: string;
  children?: React.ReactNode;
}) {
  return (
    <div className={CARD}>
      <div className="flex items-center gap-2 text-xs text-gray-500">
        <Icon size={14} /> {label}
      </div>
      <div className="mt-1 text-xl font-semibold text-gray-900 dark:text-gray-100">{value}</div>
      {children}
    </div>
  );
}

// Inline KWOTOWY editor (used for global + per-user daily limits). Limity ARON-a są
// w USD, więc pole przyjmuje grosze i polski przecinek („12,50" = „12.50").
function BudgetInput({
  initial,
  onSave,
  onCancel,
  busy,
}: {
  initial: string;
  onSave: (v: string) => void;
  onCancel: () => void;
  busy?: boolean;
}) {
  const [val, setVal] = useState(() => Number(initial || 0).toFixed(2).replace(".", ","));
  const n = parseFloat(val.replace(/\s/g, "").replace(",", "."));
  const ok = Number.isFinite(n) && n > 0 && !busy;
  const send = () => onSave(n.toFixed(2));
  return (
    <span className="inline-flex items-center gap-1">
      <input
        autoFocus
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && ok) send();
          if (e.key === "Escape") onCancel();
        }}
        className="w-24 rounded border border-gray-300 bg-white px-2 py-0.5 text-sm tabular-nums focus:border-red-500 focus:outline-none dark:border-gray-600 dark:bg-gray-800"
      />
      <span className="text-xs text-gray-400">$ / dzień</span>
      <button
        onClick={() => ok && send()}
        disabled={!ok}
        className="rounded p-1 text-emerald-600 hover:bg-emerald-50 disabled:opacity-40 dark:hover:bg-emerald-900/30"
      >
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
      </button>
      <button onClick={onCancel} className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700">
        <X size={14} />
      </button>
    </span>
  );
}

function GlobalBudgetCard({ value }: { value: string }) {
  const [editing, setEditing] = useState(false);
  const update = useUpdateGlobalBudget();
  return (
    <div className={CARD}>
      <div className="flex items-center justify-between gap-2 text-xs text-gray-500">
        <span className="flex items-center gap-2">
          <Coins size={14} /> Dzienny limit / os. (globalny, USD)
        </span>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            title="Zmień globalny limit"
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
          >
            <Pencil size={13} />
          </button>
        )}
      </div>
      <div className="mt-1">
        {editing ? (
          <BudgetInput
            initial={value}
            busy={update.isPending}
            onCancel={() => setEditing(false)}
            onSave={(v) => update.mutate(v, { onSuccess: () => setEditing(false) })}
          />
        ) : (
          <span className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            {fmtUsd(value)} $
          </span>
        )}
      </div>
    </div>
  );
}

// „Dziś vs limit" liczone w PIENIĄDZACH — kwota i procent razem, bo sam procent nie
// mówi ile to jest, a sama kwota nie mówi, jak blisko limitu. Tokeny zostają w
// podpowiedzi: nadal opisują objętość pracy, ale limitem już nie są.
function DailyBar({
  usedUsd,
  budgetUsd,
  tokens,
}: {
  usedUsd: string;
  budgetUsd: string;
  tokens: number;
}) {
  const used = parseFloat(usedUsd) || 0;
  const budget = parseFloat(budgetUsd) || 0;
  const pct = budget > 0 ? Math.min(100, Math.round((used / budget) * 100)) : 0;
  const danger = pct >= 90;
  return (
    <div
      className="flex items-center gap-2"
      title={`Dziś ${fmtUsd(used)} $ z ${fmtUsd(budget)} $ (${pct}%) · ${fmtInt(tokens)} tokenów`}
    >
      <div className="h-1.5 w-16 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-full ${danger ? "bg-red-600" : "bg-red-400"}`} style={{ width: `${pct}%` }} />
      </div>
      <span className={`text-xs tabular-nums ${danger ? "font-medium text-red-600" : "text-gray-500"}`}>
        {fmtUsd(used)} $ ({pct}%)
      </span>
    </div>
  );
}

// Per-user effective daily limit with edit + reset-to-global.
function UserBudgetControl({ row }: { row: AronCostRow }) {
  const [editing, setEditing] = useState(false);
  const setBudget = useSetUserBudget();
  const clearBudget = useClearUserBudget();
  if (editing) {
    return (
      <BudgetInput
        initial={row.daily_cost_budget_usd}
        busy={setBudget.isPending}
        onCancel={() => setEditing(false)}
        onSave={(v) =>
          setBudget.mutate(
            { userId: row.user_id, daily_cost_budget_usd: v },
            { onSuccess: () => setEditing(false) },
          )
        }
      />
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-gray-500">
      <span className="tabular-nums">limit {fmtUsd(row.daily_cost_budget_usd)} $/dzień</span>
      {row.has_budget_override && (
        <span className="rounded bg-amber-100 px-1 text-[10px] font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
          własny
        </span>
      )}
      <button
        onClick={() => setEditing(true)}
        title="Zmień limit tego użytkownika"
        className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
      >
        <Pencil size={12} />
      </button>
      {row.has_budget_override && (
        <button
          onClick={() => clearBudget.mutate(row.user_id)}
          title="Przywróć limit globalny"
          className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-gray-700"
        >
          <RotateCcw size={12} />
        </button>
      )}
    </span>
  );
}

function ModelChips({ row }: { row: AronCostRow }) {
  return (
    <div className="flex flex-wrap gap-1">
      {row.models.map((m) => (
        <span
          key={m.model_id}
          title={`${m.model_id} · ${m.conversations} rozm. · ${fmtUsd(m.cost_usd)} $`}
          className="inline-flex items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[11px] text-gray-600 dark:bg-gray-700 dark:text-gray-300"
        >
          <span className={`h-1.5 w-1.5 rounded-full ${PROVIDER_DOT[m.provider]}`} />
          {shortModel(m.model_id)}
        </span>
      ))}
    </div>
  );
}

function CostCell({ row }: { row: AronCostRow }) {
  return (
    <div className="text-right">
      <div className="font-semibold tabular-nums text-red-600">{fmtUsd(row.cost_usd)} $</div>
      <div className="mt-0.5 text-[11px] text-gray-400">
        C {fmtUsd(row.cost_claude_usd)} · DS {fmtUsd(row.cost_deepseek_usd)}
      </div>
    </div>
  );
}

function MobileRow({ row }: { row: AronCostRow }) {
  return (
    <div className={CARD}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate font-medium text-gray-900 dark:text-gray-100">{row.user_name}</div>
          <div className="truncate text-xs text-gray-400">{row.email}</div>
        </div>
        <CostCell row={row} />
      </div>
      <div className="mt-2"><ProviderSplitBar claude={row.cost_claude_usd} deepseek={row.cost_deepseek_usd} /></div>
      <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-gray-500">
        <div>Rozmowy: {row.conversation_count}</div>
        <div>Tokeny: {fmtInt(row.total_tokens)}</div>
      </div>
      <div className="mt-2"><ModelChips row={row} /></div>
      <div className="mt-2 flex items-center justify-between">
        <DailyBar
                          usedUsd={row.cost_today_usd}
                          budgetUsd={row.daily_cost_budget_usd}
                          tokens={row.tokens_today}
                        />
        <UserBudgetControl row={row} />
      </div>
    </div>
  );
}


/**
 * Rachunek za AI CAŁEJ firmy. Kafelki wyżej dotyczą wyłącznie czatu ARON, a koszt
 * wywołań AI zapisywany jest w trzynastu polach jedenastu modeli sześciu aplikacji —
 * bez tej sekcji najdroższy silnik (wyceny) nie pojawiał się w żadnym zestawieniu.
 */
function CompanyAiSpendCard() {
  const { data, isLoading, isError } = useCompanyAiSpend();
  const [open, setOpen] = useState(false);

  if (isLoading || isError || !data) return null;

  const total = Number(data.total_usd);
  const outside = Number(data.outside_chat_usd);
  const outsidePct = total > 0 ? Math.round((outside / total) * 100) : 0;
  const rows = data.rows.filter((r) => Number(r.cost_usd) > 0);
  const shown = open ? rows : rows.slice(0, 6);

  return (
    <div className="rounded-xl border border-gray-200 p-4 dark:border-gray-700">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold">Rachunek za AI — cała firma</h2>
          <p className="text-xs text-gray-500">
            Wszystkie silniki AI, nie tylko czat: {data.sources} źródeł kosztu.
          </p>
        </div>
        <div className="text-right">
          <div className="text-lg font-semibold tabular-nums text-red-600">
            {fmtUsd(total)} $
          </div>
          <div className="text-xs text-gray-500">
            poza czatem: <strong className="tabular-nums">{fmtUsd(outside)} $</strong> ({outsidePct}%)
          </div>
        </div>
      </div>

      {rows.length === 0 ? (
        <p className="mt-3 text-xs text-gray-400">Brak zarejestrowanych kosztów AI.</p>
      ) : (
        <>
          <table className="mt-3 w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wide text-gray-400">
                <th className="py-1">Silnik</th>
                <th className="py-1 text-right">Przebiegi</th>
                <th className="py-1 text-right">Koszt</th>
                <th className="py-1 text-right">Udział</th>
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => {
                const pct = total > 0 ? (Number(r.cost_usd) / total) * 100 : 0;
                return (
                  <tr key={r.key} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-1.5">{r.label}</td>
                    <td className="py-1.5 text-right tabular-nums text-gray-500">{r.runs}</td>
                    <td className="py-1.5 text-right tabular-nums font-medium">
                      {fmtUsd(Number(r.cost_usd))} $
                    </td>
                    <td className="py-1.5 text-right tabular-nums text-gray-400">
                      {pct.toFixed(1)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length > 6 && (
            <button
              onClick={() => setOpen((v) => !v)}
              className="mt-2 text-xs text-[var(--color-primary)] hover:underline"
            >
              {open ? "Pokaż mniej" : `Pokaż wszystkie (${rows.length})`}
            </button>
          )}
        </>
      )}
    </div>
  );
}

export function AronExpensesPage() {
  const { data, isLoading, isError } = useAronCosts();

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-gray-500">
        <Loader2 size={16} className="animate-spin" /> Wczytuję wydatki ARON…
      </div>
    );
  }
  if (isError || !data) {
    return <div className="p-6 text-sm text-red-500">Nie udało się wczytać wydatków ARON.</div>;
  }

  const { rows, meta } = data;

  return (
    <div className="space-y-5 p-4">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-red-600 via-red-700 to-black text-white">
          <Bot size={16} />
        </span>
        <div>
          <h1 className="text-lg font-semibold">Wydatki ARON</h1>
          <p className="text-xs text-gray-500">
            Zużycie tokenów i koszt asystenta AI w rozbiciu na pracowników (USD), z podziałem na
            dostawców (Claude / DeepSeek). Dzienny limit jest KWOTOWY — tokeny pokazujemy
            obok, ale niczego nie blokują.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <SummaryCard icon={Coins} label="Łączny koszt" value={`${fmtUsd(meta.total_cost_usd)} $`}>
          <div className="mt-2 space-y-1.5">
            <ProviderSplitBar
              claude={meta.total_cost_claude_usd}
              deepseek={meta.total_cost_deepseek_usd}
            />
            <ProviderLegend
              claude={meta.total_cost_claude_usd}
              deepseek={meta.total_cost_deepseek_usd}
            />
          </div>
        </SummaryCard>
        <SummaryCard icon={Bot} label="Łączne tokeny" value={fmtInt(meta.total_tokens)} />
        <SummaryCard icon={Users} label="Użytkownicy" value={String(meta.count)} />
        <GlobalBudgetCard value={meta.daily_cost_budget_usd} />
      </div>

      <CompanyAiSpendCard />

      {rows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 p-8 text-center text-sm text-gray-400 dark:border-gray-700">
          Brak zarejestrowanych rozmów ARON.
        </div>
      ) : (
        <>
          {/* Desktop table */}
          <div className="hidden overflow-x-auto rounded-xl border border-gray-200 md:block dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-red-50 text-left text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300">
                <tr>
                  <th className="px-4 py-2 font-medium">Pracownik</th>
                  <th className="px-4 py-2 text-right font-medium">Rozmowy</th>
                  <th className="px-4 py-2 text-right font-medium">Tokeny (we/wy)</th>
                  <th className="px-4 py-2 font-medium">Modele</th>
                  <th className="px-4 py-2 font-medium">Dziś vs limit (USD)</th>
                  <th className="px-4 py-2 text-right font-medium">Koszt (C / DS)</th>
                  <th className="px-4 py-2 font-medium">Ostatnia aktywność</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {rows.map((row) => (
                  <tr key={row.user_id} className="hover:bg-red-50/50 dark:hover:bg-red-900/10">
                    <td className="px-4 py-2">
                      <div className="font-medium text-gray-900 dark:text-gray-100">{row.user_name}</div>
                      <div className="text-xs text-gray-400">{row.email}</div>
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{row.conversation_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-gray-500">
                      {fmtInt(row.input_tokens)} / {fmtInt(row.output_tokens)}
                    </td>
                    <td className="px-4 py-2"><ModelChips row={row} /></td>
                    <td className="px-4 py-2">
                      <div className="space-y-1">
                        <DailyBar
                          usedUsd={row.cost_today_usd}
                          budgetUsd={row.daily_cost_budget_usd}
                          tokens={row.tokens_today}
                        />
                        <UserBudgetControl row={row} />
                      </div>
                    </td>
                    <td className="px-4 py-2"><CostCell row={row} /></td>
                    <td className="px-4 py-2 text-gray-500">{fmtDate(row.last_activity_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="space-y-2 md:hidden">
            {rows.map((row) => (
              <MobileRow key={row.user_id} row={row} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
